import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';

import useAuth from '../hooks/useAuth';
import { CoachProvider, useCoach } from '../onboarding/engine/CoachProvider';
import {
  OnboardingStateProvider,
  nextInJourney,
  useOnboardingState,
} from '../onboarding/state';
import { ADMIN_TOUR_GUIDE_ID, ADMIN_TOUR_SLUG, isTourDone } from '../onboarding/adminTour';
import { canRunGuide } from '../onboarding/guides';
import {
  GUIDE_STATUS,
  normalizeProgress,
  persistProgress,
  setProgressSyncHandler,
} from '../onboarding/progress';

// ---------------------------------------------------------------------------
// Everything the admin console needs to know about onboarding, in one context.
//
// Three layers stack here, outermost first:
//
//   OnboardingStateProvider  who the user is (persona), what their journey is,
//                            and how ready their space actually is
//   CoachProvider            the engine that runs a guide
//   AdminOnboardingBridge    the glue: auto-launch, dismissal, legacy card API
//
// Two distinctions this file exists to keep straight:
//
//   push vs pull. `eligible` gates the PUSH - the one automatic launch a new
//   admin gets, and the card by default. It never gates the engine. Any org
//   user can pull a guide from the help menu, from Profile or from Dex,
//   including every customer who predates onboarding and must never be
//   ambushed by it. `cardVisible` is the card's own question, and it says yes
//   to anyone who explicitly asked for the card back even when the cohort rule
//   would never have pushed it at them.
//
//   server vs local progress. The server hands down a snapshot of guide progress
//   when state is fetched; persistProgress writes locally as a guide runs. The
//   local copy is the fresher of the two, so it wins in `guidesProgress`.
// ---------------------------------------------------------------------------

const AdminOnboardingContext = createContext(null);

const FALLBACK = {
  eligible: false,
  available: false,
  cardVisible: false,
  persona: null,
  personaLabel: '',
  journey: { quick: [], advanced: [] },
  guidesProgress: {},
  readiness: null,
  progress: normalizeProgress(null),
  running: false,
  startTour: () => {},
  resumeTour: () => {},
  startGuide: () => {},
  canRunGuide: () => false,
  dismiss: () => {},
  restoreCard: () => {},
  refresh: () => {},
};

export function useAdminOnboarding() {
  return useContext(AdminOnboardingContext) || FALLBACK;
}

function AdminOnboardingBridge({ children }) {
  const location = useLocation();
  const { user, updateUser } = useAuth();
  const coach = useCoach();
  const state = useOnboardingState();
  const autoLaunchedRef = useRef(false);

  // Keep user.onboarding_progress fresh as the engine writes, so a re-render
  // reflects the new step without a re-login.
  const userRef = useRef(user);
  userRef.current = user;
  useEffect(
    () =>
      setProgressSyncHandler((map) => {
        const { current } = userRef;
        if (updateUser && current) updateUser({ ...current, onboarding_progress: map });
      }),
    [updateUser]
  );

  // Server snapshot under, live local writes over.
  const guidesProgress = useMemo(
    () => ({ ...(state.guides || {}), ...(user?.onboarding_progress || {}) }),
    [state.guides, user?.onboarding_progress]
  );

  // Checklist state (dismissed, ticked rows) still lives under the legacy slug
  // so nobody's existing card state is thrown away.
  const progress = useMemo(
    () => normalizeProgress(user?.onboarding_progress?.[ADMIN_TOUR_SLUG]),
    [user?.onboarding_progress]
  );

  const startGuide = useCallback((guideId, at = 0) => coach.start(guideId, { at }), [coach]);

  // The quick tour is whatever the backend put first in this persona's quick
  // journey. It falls back to the orientation guide so the button is never dead
  // while a persona's journey is still being written.
  const quickEntry = useMemo(
    () => nextInJourney(state.journey?.quick, guidesProgress),
    [state.journey, guidesProgress]
  );

  const startTour = useCallback(() => {
    const id = state.journey?.quick?.[0] || ADMIN_TOUR_GUIDE_ID;
    coach.start(id, { at: 0 });
  }, [coach, state.journey]);

  const resumeTour = useCallback(() => {
    if (!quickEntry) {
      startTour();
      return;
    }
    coach.start(quickEntry.guide.id, { at: quickEntry.progress.current_step || 0 });
  }, [coach, quickEntry, startTour]);

  const dismiss = useCallback(() => {
    coach.stop();
    persistProgress(ADMIN_TOUR_SLUG, { dismissed: true });
  }, [coach]);

  // The other half of dismiss, reachable from Profile. Writing an explicit
  // false rather than clearing the key is deliberate - see `restored` in
  // progress.js. Nothing is started here: the user asked for the card back, not
  // to be dropped into a tour.
  const restoreCard = useCallback(() => {
    persistProgress(ADMIN_TOUR_SLUG, { dismissed: false });
  }, []);

  // Auto-launch: the cohort rule is the server's call (`auto_launch`), and it is
  // already false for everyone who joined before onboarding shipped. Fires once,
  // only from the dashboard, so nobody is yanked out of a task mid-way.
  const eligible = state.available && state.autoLaunch;

  // What the dashboard card asks. Push eligibility gets you the card by
  // default, but so does asking for it, which is the only way a customer who
  // predates onboarding can ever see the checklist. Auto-launch stays on
  // `eligible` alone: restoring the card must not also start a tour.
  const cardVisible = state.available && (eligible || progress.restored) && !progress.dismissed;

  useEffect(() => {
    if (!eligible || autoLaunchedRef.current || progress.dismissed || coach.running) return;
    if (!location.pathname.includes('/admin/dashboard')) return;
    if (!quickEntry || quickEntry.progress.status !== GUIDE_STATUS.notStarted) return;
    autoLaunchedRef.current = true;
    coach.start(quickEntry.guide.id, { at: 0 });
  }, [eligible, progress.dismissed, location.pathname, quickEntry, coach]);

  const value = useMemo(
    () => ({
      eligible,
      cardVisible,
      available: state.available,
      persona: state.persona,
      personaLabel: state.personaLabel,
      journey: state.journey,
      guidesProgress,
      readiness: state.readiness,
      progress,
      running: coach.running,
      startTour,
      resumeTour,
      startGuide,
      // So a caller can leave out a "Show me" button rather than render one
      // that refuses. The engine still enforces it either way.
      canRunGuide: coach.canRun,
      dismiss,
      restoreCard,
      refresh: state.refresh,
    }),
    [
      eligible, cardVisible, state, guidesProgress, progress, coach.running,
      coach.canRun, startTour, resumeTour, startGuide, dismiss, restoreCard,
    ]
  );

  return <AdminOnboardingContext.Provider value={value}>{children}</AdminOnboardingContext.Provider>;
}

AdminOnboardingBridge.propTypes = { children: PropTypes.node };

// A finished guide can flip a readiness item green (creating a plan is both a
// guide and a checklist item), so the state is refetched the moment one ends.
function CoachWithRefresh({ children }) {
  const { refresh } = useOnboardingState();
  const { user, hasPermission } = useAuth();
  const onGuideFinished = useCallback(() => refresh(), [refresh]);

  // The engine deliberately knows nothing about permissions, so the app hands
  // it the check. Every start goes through here, including a link somebody was
  // sent, which is the only path where the guide was not picked off a list this
  // user's own codes produced.
  const features = user?.orgPlan?.features;
  const canRun = useCallback(
    (guideId) =>
      canRunGuide(guideId, {
        hasPermission,
        hasFeature: (slug) => !!features?.some((f) => f.slug === slug),
      }),
    [hasPermission, features]
  );

  return (
    <CoachProvider onGuideFinished={onGuideFinished} canRun={canRun}>
      {children}
    </CoachProvider>
  );
}

CoachWithRefresh.propTypes = { children: PropTypes.node };

export function AdminOnboardingProvider({ children }) {
  return (
    <OnboardingStateProvider>
      <CoachWithRefresh>
        <AdminOnboardingBridge>{children}</AdminOnboardingBridge>
      </CoachWithRefresh>
    </OnboardingStateProvider>
  );
}

AdminOnboardingProvider.propTypes = { children: PropTypes.node };

export { isTourDone };
